import React, { useEffect, useState } from 'react';
import { NextUI } from '@/components/NextUI';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import useCheckout from '@/features/subscription/hooks/useCheckout';
import { useCreateTransaction } from '@/features/subscription/hooks/useCreateTransaction';
import usePaddlePricing from '@/features/subscription/hooks/usePaddlePricing';
import useAuth from '@/hooks/useAuth';
import clsx from 'clsx';
import { usePayment } from '@/features/subscription/hooks/usePayment';
import {
  setPaymentDetails,
  resetPaymentDetails,
  selectPayment,
} from '@/store/slices/paymentSlice';
import FilePreviewPanel from './FilePreviewPanel';
import {
  APP_EVENTS,
  COMMON_EVENTS,
  getUserData,
  logHubxEvent,
} from '@/app/providers/analytics';
import useUpdateEffect from '@/hooks/useUpdateEffect';
// import { analytics } from '@/lib/firebaseConfig';
// import { logEvent } from 'firebase/analytics';
import { RootState } from '@/store';
import { formatAnalyticsSource } from '@/helpers/common';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import { CloseIcon } from '@heroui/shared-icons';
import { resolveProcessPreviewExtension } from '../../utils/resolveProcessPreviewExtension';
import { resolveAnalyticsPaywallType } from '@/features/subscription/utils/analyticsPaywall';
import { useCheckoutReturn } from '@/features/subscription/hooks/useCheckoutReturn';
import { useSingleTrigger } from '@hubxco/hubx-smartlink';
import {
  getRecurringPriceForSelectedPrice,
  getSelectedPaywallPrice,
} from '@/features/subscription/utils/pricing/paywallPricing';
import { checkoutRevealStore } from '@/features/subscription/utils/checkout/checkoutRevealStore';

const PaddlePaymentModal = ({
  data,
  hideModal,
}: {
  data?: any;
  hideModal: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const typeForUserAccessLevel = useAppSelector(
    (state: RootState) => state.userAccess.typeForUserAccessLevel
  );
  const { monthlyPrices, weeklyPrices, yearlyPrices, isLoadingPrices } =
    usePaddlePricing();
  const { isCheckoutOpen, openCheckout, updateCheckout, closeCheckout } =
    useCheckout();
  const { clearTransaction } = useCreateTransaction(
    typeForUserAccessLevel || 'in_app'
  );
  const { selectedPriceId, seats } = useAppSelector(selectPayment);
  const { paymentStatus, updatePaymentStatus } = usePayment();
  // const { hadFreeTrial } = usePaddlePricing();
  const { files, exportAsFiles } = useAppSelector((state: any) => state.tool);
  const [hasLoggedCheckout, setHasLoggedCheckout] = useState(false);
  const checkoutLoadStartRef = React.useRef<number | null>(null);
  const checkoutContainerRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    checkoutRevealStore.setRevealTarget(checkoutContainerRef.current);
    return () => checkoutRevealStore.setRevealTarget(null);
  }, []);
  const previewFile = (files && files.length > 0 ? files[0] : null) || data?.file || null;
  const previewProcessedFile =
    (exportAsFiles && exportAsFiles.length > 0 ? exportAsFiles[0] : null) ||
    data?.processedFile ||
    null;
  const previewExtension = resolveProcessPreviewExtension({
    extension: data?.extension,
    toolType: data?.toolType,
    file: previewFile,
    processedFile: previewProcessedFile,
  });
  const { requestReturn, runPendingReturn } = useCheckoutReturn({
    isEnabled: !data?.isOnlyPayment && !data?.openedFromProceedPopup,
    file: previewProcessedFile || previewFile,
    fileName: previewProcessedFile?.name || previewFile?.name || '',
    extension: previewExtension,
    toolType: data?.toolType,
    originEntryPoint: data?.originEntryPoint,
  });
  const formattedToolType = formatAnalyticsSource(data?.toolType);
  const analyticsSource = buildStructuredAnalyticsSource(
    data?.toolType,
    data?.originEntryPoint
  );
  const paywallType = resolveAnalyticsPaywallType({
    paywallType: typeForUserAccessLevel,
    toolType: data?.toolType,
  });
  const selectedCheckoutPrice = React.useMemo(
    () =>
      getSelectedPaywallPrice({
        selectedPlan: data?.selectedPlan,
        monthlyPrices,
        weeklyPrices,
        yearlyPrices,
        user,
      }),
    [data?.selectedPlan, monthlyPrices, weeklyPrices, yearlyPrices, user]
  );
  const recurringCheckoutPrice = React.useMemo(
    () =>
      getRecurringPriceForSelectedPrice({
        selectedPrice: selectedCheckoutPrice,
        monthlyPrices,
        weeklyPrices,
        yearlyPrices,
      }),
    [selectedCheckoutPrice, monthlyPrices, weeklyPrices, yearlyPrices]
  );

  useSingleTrigger(() => {
    logHubxEvent({
      eventName: APP_EVENTS.LND_CHECKOUT,
      ...(data?.toolType
        ? { source: formattedToolType }
        : {}),
      event_params: data?.toolType
        ? {
            source: analyticsSource,
          }
        : undefined,
    });

    logHubxEvent({
      eventName: COMMON_EVENTS.LND_PAYWALL,
      ...(data?.toolType ? { source: formattedToolType } : {}),
      event_params: {
        ...(data?.toolType ? { source: analyticsSource } : {}),
        paywallType,
      } as any,
    });
  });

  // Reset payment state when modal first opens
  useEffect(() => {
    updatePaymentStatus('idle');
    dispatch(resetPaymentDetails());
    clearTransaction();

    return () => {
      dispatch(resetPaymentDetails());
      closeCheckout();
    };
  }, []);

  // Set payment details based on selected plan whenever prices or selectedPlan changes
  useEffect(() => {
    if (selectedCheckoutPrice?.id) {
      dispatch(
        setPaymentDetails({
          selectedPriceId: selectedCheckoutPrice.id,
          interval:
            selectedCheckoutPrice.billing_cycle?.interval === 'year'
              ? 'year'
              : selectedCheckoutPrice.billing_cycle?.interval === 'week'
                ? 'week'
                : 'month',
          seats: 1,
        })
      );
    }
  }, [dispatch, selectedCheckoutPrice]);

  useEffect(() => {
    if (
      paymentStatus === 'processing' ||
      paymentStatus === 'verifying' ||
      paymentStatus === 'completed'
    ) {
      return;
    }

    if (
      user &&
      data?.selectedPlan &&
      !isCheckoutOpen &&
      !isLoadingPrices
    ) {
      const checkoutContainer = document.querySelector('.checkout-container');
      if (checkoutContainer) {
        const priceId = selectedCheckoutPrice?.id;

        if (priceId) {
          checkoutLoadStartRef.current = Date.now();
          closeCheckout();
          openCheckout({
            priceId: priceId,
            quantity: seats || 1,
            toolType: data?.toolType,
            paywallType,
          });
        }
      }
    } else if (
      user &&
      data?.selectedPlan &&
      isCheckoutOpen &&
      !isLoadingPrices
    ) {
      const priceId = selectedCheckoutPrice?.id;

      if (priceId) {
        checkoutLoadStartRef.current = Date.now();
        updateCheckout({
          priceId: priceId,
          quantity: seats || 1,
          toolType: data?.toolType,
          paywallType,
        });
      }
    }
  }, [
    data?.selectedPlan,
    seats,
    isCheckoutOpen,
    user,
    isLoadingPrices,
    paymentStatus,
    paywallType,
    selectedCheckoutPrice?.id,
  ]);

  useEffect(() => {
    if (paymentStatus === 'completed') {
      const timer = setTimeout(() => {
        handleClose();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  useUpdateEffect(() => {
    if (
      user &&
      selectedPriceId &&
      isCheckoutOpen &&
      !isLoadingPrices &&
      paymentStatus === 'checkout_ready'
    ) {
      const analyticsUserData = getUserData(user);
      const checkoutLoadDuration = checkoutLoadStartRef.current
        ? Date.now() - checkoutLoadStartRef.current
        : undefined;

      if (!hasLoggedCheckout) {
        logHubxEvent({
          eventName: COMMON_EVENTS.CHECKOUT_LOADED,
          event_params: {
            amount: checkoutLoadDuration,
            info: 'success',
            paywallType,
            product_id: selectedCheckoutPrice?.product_id,
            currency:
              recurringCheckoutPrice?.unit_price?.currency_code ??
              selectedCheckoutPrice?.unit_price?.currency_code,
          },
        });

        // if (freeTypeForUserAccessLevel) {
        //   setTimeout(() => {
        //     logHubxEvent({
        //       eventName: COMMON_EVENTS.CHECKOUT_LOADED,
        //       event_params: {
        //         info: 'success',
        //         paywallType: freeTypeForUserAccessLevel,
        //         product_id: selectedPriceId,
        //         currency: 'USD',
        //       },
        //     });
        //   }, 1000);
        // }

        // logHubxEvent({
        //   eventName: COMMON_EVENTS.LND_PAYWALL,
        //   event_params: { paywallType: typeForUserAccessLevel },
        // });

        const gtmPayload = {
          event_name: 'lnd_paywall',
          externalId: analyticsUserData.external_id,
          firstName: analyticsUserData.first_name,
          lastName: analyticsUserData.last_name,
          email: analyticsUserData.email,
        };

        // logHubxEvent({
        //   eventName: COMMON_EVENTS.PAYWALL_SEEN,
        //   event_params: { paywallType: typeForUserAccessLevel },
        // });
        // if (freeTypeForUserAccessLevel) {
        //   setTimeout(() => {
        //     logHubxEvent({
        //       eventName: COMMON_EVENTS.LND_PAYWALL,
        //       event_params: {
        //         paywallType: freeTypeForUserAccessLevel,
        //       },
        //     });
        //     logHubxEvent({
        //       eventName: COMMON_EVENTS.PAYWALL_SEEN,
        //       event_params: { paywallType: freeTypeForUserAccessLevel },
        //     });
        //   }, 1000);
        // }

        // if (analytics) {
        //   logEvent(analytics, 'lnd_paywall', {
        //     paywallType: typeForUserAccessLevel,
        //   });

          // if (freeTypeForUserAccessLevel) {
          //   setTimeout(() => {
          //     logEvent(analytics!, 'lnd_paywall', {
          //       paywallType: freeTypeForUserAccessLevel,
          //     });
          //   }, 1000);
          // }
        // }

        // if (typeof window !== 'undefined' && (window as any).dataLayer) {
        //   const dataLayerPayload = {
        //     event: 'lnd_paywall_v2',
        //     internal_use: false,
        //     user_data: analyticsUserData,
        //     paywallType: typeForUserAccessLevel,
        //   };

        //   (window as any).dataLayer.push(dataLayerPayload);
          // if (freeTypeForUserAccessLevel) {
          //   setTimeout(() => {
          //     (window as any).dataLayer.push({
          //       event: 'lnd_paywall_v2',
          //       internal_use: false,
          //       user_data: analyticsUserData,
          //       paywallType: freeTypeForUserAccessLevel,
          //     });
          //   }, 1000);
          // }
        // }
        setHasLoggedCheckout(true);
      }
    }
  }, [
    hasLoggedCheckout,
    isCheckoutOpen,
    isLoadingPrices,
    paywallType,
    paymentStatus,
    recurringCheckoutPrice?.unit_price?.currency_code,
    selectedCheckoutPrice?.product_id,
    selectedCheckoutPrice?.unit_price?.currency_code,
  ]);

  const handleClose = () => {
    const isCancelled = paymentStatus !== 'completed';
    if (isCancelled) {
      data?.onCancel?.();
      requestReturn();
    }
    closeCheckout();
    hideModal();
    runPendingReturn();
  };

  const handleCloseClick = () => {
    logHubxEvent({
      eventName: COMMON_EVENTS.BTN_PAYWALL_CLOSE,
      event_params: {
        paywallType,
      },
    });
    handleClose();
  };

  const handleDismiss = () => {
    logHubxEvent({
      eventName: COMMON_EVENTS.BTN_PAYWALL_CLOSE,
      event_params: {
        paywallType,
      },
    });
    handleClose();
  };

  return (
    <NextUI.Modal
      className="!mx-[20px] xl:max-w-[1340px] max-w-[1000px] max-lg:w-full w-[95vw] max-h-[95vh] !my-0"
      classNames={{ content: 'max-lg:px-4 p-10 !overflow-y-auto overscroll-y-none' }}
      defaultOpen={true}
      name="PaddlePayment"
      backdrop="blur"
      scrollBehavior="inside"
      hideCloseButton={true}
      onClose={handleDismiss}
    >
      <div className="flex flex-col bg-white">
        <button
          type="button"
          aria-label="Close"
          onClick={handleCloseClick}
          className="absolute top-10 right-10 text-[1.25rem]"
        >
          <CloseIcon />
        </button>
        {/* Header */}
        <div className="flex justify-between items-center pb-6">
          <h2 className="text-2xl font-bold text-dark">Payment details</h2>
        </div>

        {/* Stepper */}
        {data?.isOnlyPayment?
        <div className="flex items-center justify-start gap-4 text-sm font-medium max-lg:hidden mb-8">
          
          <div className="flex items-center gap-2 text-primary-blue">
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.5 0C5.59644 0 0 5.59644 0 12.5C0 19.4036 5.59644 25 12.5 25C19.4036 25 25 19.4036 25 12.5C25 5.59644 19.4036 0 12.5 0ZM17.2174 10.1666C17.6546 9.63228 17.5759 8.84475 17.0415 8.40759C16.5072 7.97043 15.7197 8.04918 15.2826 8.58349L10.5322 14.3895L9.00888 12.8662C8.52073 12.378 7.72927 12.378 7.24112 12.8662C6.75296 13.3543 6.75296 14.1458 7.24112 14.6339L9.74112 17.1339C9.99084 17.3836 10.3345 17.5161 10.6873 17.4985C11.04 17.4809 11.3688 17.3149 11.5924 17.0416L17.2174 10.1666Z"
                fill="#4B7EFF"
              />
            </svg>
            <span className="text-[#6D6D6D] text-[17px]">Select plan</span>
          </div>

          <div className="w-12 h-[1px] bg-[#CACACA]" />

          <div className="flex items-center gap-2">
            <span className="w-[25px] h-[25px] rounded-full bg-primary-blue text-white flex items-center justify-center text-[16px]">
              2
            </span>
            <span className="text-[#6D6D6D] text-[17px]">Payment details</span>
          </div>
        </div>
        :
        <div className="flex items-center justify-start gap-4 text-sm font-medium max-lg:hidden mb-8">
          <div className="flex items-center gap-2 text-primary-blue">
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.5 0C5.59644 0 0 5.59644 0 12.5C0 19.4036 5.59644 25 12.5 25C19.4036 25 25 19.4036 25 12.5C25 5.59644 19.4036 0 12.5 0ZM17.2174 10.1666C17.6546 9.63228 17.5759 8.84475 17.0415 8.40759C16.5072 7.97043 15.7197 8.04918 15.2826 8.58349L10.5322 14.3895L9.00888 12.8662C8.52073 12.378 7.72927 12.378 7.24112 12.8662C6.75296 13.3543 6.75296 14.1458 7.24112 14.6339L9.74112 17.1339C9.99084 17.3836 10.3345 17.5161 10.6873 17.4985C11.04 17.4809 11.3688 17.3149 11.5924 17.0416L17.2174 10.1666Z"
                fill="#4B7EFF"
              />
            </svg>
            <span className="text-[#6D6D6D] text-[17px]">
              Document is ready
            </span>
          </div>

          <div className="w-12 h-[1px] bg-[#CACACA]" />

          <div className="flex items-center gap-2 text-primary-blue">
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.5 0C5.59644 0 0 5.59644 0 12.5C0 19.4036 5.59644 25 12.5 25C19.4036 25 25 19.4036 25 12.5C25 5.59644 19.4036 0 12.5 0ZM17.2174 10.1666C17.6546 9.63228 17.5759 8.84475 17.0415 8.40759C16.5072 7.97043 15.7197 8.04918 15.2826 8.58349L10.5322 14.3895L9.00888 12.8662C8.52073 12.378 7.72927 12.378 7.24112 12.8662C6.75296 13.3543 6.75296 14.1458 7.24112 14.6339L9.74112 17.1339C9.99084 17.3836 10.3345 17.5161 10.6873 17.4985C11.04 17.4809 11.3688 17.3149 11.5924 17.0416L17.2174 10.1666Z"
                fill="#4B7EFF"
              />
            </svg>
            <span className="text-[#6D6D6D] text-[17px]">Select plan</span>
          </div>

          <div className="w-12 h-[1px] bg-[#CACACA]" />

          <div className="flex items-center gap-2">
            <span className="w-[25px] h-[25px] rounded-full bg-primary-blue text-white flex items-center justify-center text-[16px]">
              3
            </span>
            <span className="text-[#6D6D6D] text-[17px]">Payment details</span>
          </div>

          <div className="w-12 h-[1px] bg-[#CACACA]" />

          <div className="flex items-center gap-2 text-[#6D6D6D]">
            <span className="border border-[#D3E6F4] bg-[#F1F9FF] w-[25px] h-[25px] rounded-full flex items-center justify-center text-[16px]">
              4
            </span>
            <span className="text-[#6D6D6D] text-[17px]">Download</span>
          </div>
        </div>}
        

        {/* Content */}
        <div className={clsx("flex flex-col-reverse lg:flex-row gap-8 justify-center",data?.isOnlyPayment&&"items-center")}>
          {/* Left Preview */}

{data?.isOnlyPayment?<></>:
          <div className="w-full lg:flex-1 max-lg:h-[600px] rounded-xl flex flex-col p-8 border border-[#ECECEC]">
            <FilePreviewPanel
              file={previewFile}
              processedFile={previewProcessedFile}
              extension={previewExtension}
            />
          </div>
}
          {/* Right Paddle */}
          <div className={clsx(data?.isOnlyPayment&&"max-w-[500px]","w-full lg:flex-1 flex")}>
            <div className="w-full rounded-xl border border-[#ECECEC] p-6 bg-white relative overflow-hidden">
              {paymentStatus === 'verifying' && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/90 text-center">
                  <NextUI.Spinner />
                  <span className="text-[15px] text-[#6D6D6D]">
                    We are checking your payment status...
                  </span>
                </div>
              )}
              <div ref={checkoutContainerRef} />
            </div>
          </div>
        </div>
      </div>
    </NextUI.Modal>
  );
};

export default PaddlePaymentModal;
